import React, { useEffect, useState } from 'react';
import { FaDownload, FaTrash } from 'react-icons/fa';
import { Button, Text } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import AsyncImg from '../../../components/extras/AsyncImg';
import useHandleDownloadDocument from '../../../hooks/useDownloadDocument';
import { DocumentsService } from '../../../services/document/documentsService';
import { RefuelService } from '../../../services/refuels/refuelService';
import { CreatedAt } from '../../../type/apiResponse-type';

interface RefuelDocument {
    id: string;
    document: {
        id: string;
        originalName: string;
        url: string;
        mimeType: string;
        createdAt: CreatedAt;
        subdirectory: string;
        fileName: string;
        extension: string;
        status: string;
    };
    user?: {
        id: string;
        name: string;
        lastName: string;
        email: string;
        dni: string;
    };
    createdAt: string;
}

interface RefuelDocumentsViewerProps {
    refuelId: string;
    onDocumentDeleted?: () => void;
    columns?: number;
    disableDelete?: boolean;
}

const RefuelDocumentsViewer: React.FC<RefuelDocumentsViewerProps> = ({
    refuelId,
    onDocumentDeleted,
    columns = 2,
    disableDelete = false
}) => {
    const [documents, setDocuments] = useState<RefuelDocument[]>([]);
    const [loading, setLoading] = useState(false);

    // Hooks
    const { handleDownloadDocumentNewTab } = useHandleDownloadDocument();
    const documentsService = new DocumentsService();
    const refuelService = new RefuelService();

    const fetchDocuments = async () => {
        try {
            setLoading(true);
            const service = new RefuelService();
            const response = await service.getDocumentsByRefuel(refuelId);
            const result = response.getResponse();

            if (result?.status === 200 && result.data?.success) {
                setDocuments(result.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching documents:', error);
            toast.error('Error al cargar los documentos');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (refuelId) {
            fetchDocuments();
        }
    }, [refuelId]);


    const handleDownload = async (documentId: string) => {
        try {
            const document = documents.find(doc => doc.document.id === documentId);
            if (document) {
                await handleDownloadDocumentNewTab(
                    documentsService.downloadDocument,
                    documentId,
                    document.document.originalName,
                    document.document.extension
                );
            }
        } catch (error) {
            toast.error('Error al descargar el documento');
        }
    };

    const handleDelete = async (documentId: string) => {
        try {
            await refuelService.removeRefuelDocument(refuelId, [documentId]);
            toast.success('Documento eliminado correctamente');
            fetchDocuments();
        } catch (error) {
            toast.error('Error al eliminar el documento');
        }
    };


    if (loading) {
        return (
            <div className="text-center py-4">
                <Text>Cargando documentos...</Text>
            </div>
        );
    }

    if (documents.length === 0) {
        return (
            <div className="text-center py-4">
                <Text className="text-gray-500">No hay documentos asociados a este repostaje</Text>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <Text className="text-lg font-semibold">Documentos del repostaje</Text>

            <div className={`grid gap-4 ${columns === 1 ? 'grid-cols-1' :
                columns === 2 ? 'grid-cols-1 md:grid-cols-2' :
                    columns === 3 ? 'grid-cols-2 md:grid-cols-3' :
                        columns === 4 ? 'grid-cols-2 md:grid-cols-4' :
                            'grid-cols-2'
                }`}>
                {documents.map((doc) => (
                    <div key={doc.id} className="border rounded-lg p-3 bg-white shadow-sm">
                        <div className="aspect-square mb-2 bg-gray-100 rounded-lg overflow-hidden">
                            {doc.document.id ? (
                                <AsyncImg
                                    id={doc.document.id}
                                    className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gray-200">
                                    <Text className="text-gray-500 text-sm text-center">
                                        {doc.document.originalName}
                                    </Text>
                                </div>
                            )}
                        </div>

                        <div className="space-y-1">
                            <Text className="text-sm font-medium truncate" title={doc.document.originalName}>
                                {doc.document.originalName}
                            </Text>

                            {doc.user && (
                                <Text className="text-xs text-gray-500">
                                    Subido por: {doc.user.name}
                                </Text>
                            )}
                            <Text className="text-xs text-gray-400">
                                {new Date(doc.document?.createdAt?.date || '').toLocaleDateString()}
                            </Text>
                        </div>

                        <div className="flex gap-2 mt-2">
                            {!disableDelete && doc.document.id && (
                                <DeletePopover
                                    title="Eliminar documento"
                                    description={`¿Estás seguro de que deseas eliminar el documento "${doc.document.originalName}"?`}
                                    onDelete={() => handleDelete(doc.document.id)}
                                    Icon={<FaTrash className="w-3 h-3" />}
                                    actionIconClassName="h-auto px-3 w-full flex-1 text-white"
                                />
                            )}
                            {doc.document.id && (
                                <Button
                                    size="sm"
                                    variant="solid"
                                    className="flex-1 bg-black text-white hover:text-white"
                                    onClick={() => handleDownload(doc.document.id)}
                                    title="Descargar documento"
                                >
                                    <FaDownload className="w-3 h-3" />
                                </Button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

        </div>
    );
};

export default RefuelDocumentsViewer;